import { GetServerSideProps, NextPage } from 'next';
import { PATHS, PageViewTracker, template } from '@belong/common';
import { Divider, Paginator, Text } from '@belong/ui';
import { MetaSEO } from 'src/components/meta-seo/meta-seo';
import { BlogCard } from 'src/features/blog/components/card/card';
import {
  fetchAllBlogProfiles,
  fetchBlogPostsList,
} from 'src/features/cms/prismic';
import { createClient } from 'src/prismicio';
import { BLOG_AUTHOR_STRINGS } from '../../../features/blog/blog.strings';
import { AboutAuthor } from '../../../features/blog/components/about-author/about-author';
import { AuthorsList } from '../../../features/blog/components/blog-authors-list/AuthorsList';
import type { BlogAuthor } from '../../../features/blog/constants';
import type { BlogPost as BlogPostDocument } from '../../../features/blog/types';
import type { AuthorUser } from '../[uid]';

type PageProps = {
  authorData: AuthorUser;
  // The Prismic document shape, not `[uid]`'s narrower `BlogPost`:
  // `fetchBlogPostsList` returns whole documents and `BlogCard` expects them.
  postsByAuthor: BlogPostDocument[];
  otherAuthors: BlogAuthor[];
  pageCount: number;
  page: number;
};

const BlogAuthorPage: NextPage<PageProps> = ({
  authorData,
  otherAuthors,
  postsByAuthor,
  page,
  pageCount,
}) => {
  const authorFullName =
    `${authorData.firstName} ${authorData.lastName || ''}`.trim();
  const additionalLinkTags = [];
  const metaTitle =
    page > 1
      ? template(BLOG_AUTHOR_STRINGS['metaTitleWithPagination'], {
          authorFullName: authorFullName,
          page,
        })
      : template(BLOG_AUTHOR_STRINGS['metaTitle'], {
          authorFullName: authorFullName,
        });
  const metaDescription =
    page > 1
      ? template(BLOG_AUTHOR_STRINGS['metaDescriptionWithPagination'], {
          aboutAuthor: authorData.aboutMe,
          page,
        })
      : authorData.aboutMe;

  const path = PATHS.ABOUT_AUTHOR.replace(':authorId', authorData.uid);
  if (page !== 1) {
    additionalLinkTags.push({
      rel: 'prev',
      href: `https://belonghome.com${path}?page=${page - 1}`,
    });
  }

  if (page !== pageCount) {
    additionalLinkTags.push({
      rel: 'next',
      href: `https://belonghome.com${path}?page=${page + 1}`,
    });
  }

  const getPageLink = (page = 1) => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', `${page}`);
    return `/blog/author/${authorData.uid}?${searchParams.toString()}`;
  };

  return (
    <PageViewTracker name="blog_author">
      <MetaSEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
        }}
        additionalCanonicalPath={`?page=${page}`}
        additionalLinkTags={additionalLinkTags}
      />

      <div className="pt-lg md:pt-3xl container">
        <div className="grid lg:grid-cols-3 ">
          <div className="lg:max-w-flow-lg col-span-2">
            <AboutAuthor
              variant="large"
              // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
              // `authorData` is the camelCased projection built in
              // `getServerSideProps`, so `AboutAuthor` — which reads
              // `profile_image_url` and `first_name` — finds neither and has
              // never rendered the image `showImg` asks for. Same mismatch as
              // the two instances on the blog post page.
              author={authorData}
              firstLine={
                <Text
                  as="h1"
                  fontWeight="semibold"
                  className="text-h3-xl lg:text-h2-xl"
                >
                  {authorFullName}
                </Text>
              }
              secondLine={null}
              showImg
            />
            <Text as="h2" className="mt-xl">
              {authorData.aboutMe}
            </Text>
            <div className="mt-2xl">
              <Text
                fontWeight="semibold"
                className="mb-lg text-h3-sm lg:mb-xl "
              >
                {template(BLOG_AUTHOR_STRINGS['authorName'], {
                  authorName: authorFullName,
                })}
              </Text>
              {postsByAuthor.map((post) => (
                <div className="mb-xl" key={post.uid}>
                  <BlogCard post={post} variant="horizontal" />
                  <Divider className="mt-sm" />
                </div>
              ))}
            </div>
          </div>
          <div className="hidden lg:block">
            <AuthorsList blogAuthors={otherAuthors} />
          </div>
        </div>
        <div className="mb-3xl mt-sm">
          {pageCount > 1 && (
            <Paginator
              page={page}
              pageCount={pageCount}
              getPageLink={getPageLink}
            />
          )}
        </div>
        <div className="mb-2xl lg:hidden">
          <AuthorsList blogAuthors={otherAuthors} />
        </div>
      </div>
    </PageViewTracker>
  );
};

export default BlogAuthorPage;

// The `catch` at the end of this function logs and returns nothing, so it can
// resolve to `undefined`, which Next rejects at request time with "did not
// return an object". Filling in a `notFound` or fallback is the TODO in that
// catch; choosing one here would change which status the page serves.
// @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
export const getServerSideProps: GetServerSideProps = async ({
  query,
  previewData,
}) => {
  const client = createClient({ previewData });
  const { authorSlug, page } = query;
  const pageNumber = page ? Number(page) : 1;

  try {
    const allAuthors = await fetchAllBlogProfiles(client);
    const selectedAuthor = allAuthors.find(
      (author) => author.uid === authorSlug,
    );

    if (!selectedAuthor) {
      return {
        notFound: true,
      };
    }

    function generateRandomNumbers(length: number, amount: number) {
      const randomNumbers: number[] = [];

      while (randomNumbers.length < amount) {
        const randomNumber = Math.floor(Math.random() * length);

        if (!randomNumbers.includes(randomNumber)) {
          randomNumbers.push(randomNumber);
        }
      }
      return randomNumbers;
    }

    // get the authors that are not the selected one
    const otherAuthors = allAuthors.filter(
      (author) => author.uid !== authorSlug,
    );
    const randomAuthorIndices = generateRandomNumbers(otherAuthors.length, 5);

    const { results, total_pages } = await fetchBlogPostsList({
      page: pageNumber,
      client,
      pageProps: { pageSize: 4 },
      authors: [authorSlug as string],
    });

    const authorData = {
      uid: selectedAuthor?.uid,
      firstName: selectedAuthor?.data?.first_name,
      lastName: selectedAuthor?.data?.last_name || null,
      aboutMe: selectedAuthor?.data?.about_me || null,
      title: selectedAuthor?.data?.title || null,
      profileImageUrl: selectedAuthor?.data?.profile_image_url?.url || null,
    };

    return {
      props: {
        authorData,
        postsByAuthor: results,
        otherAuthors: randomAuthorIndices.map((index) => otherAuthors[index]),
        pageCount: total_pages,
        page: pageNumber,
      },
    };
  } catch (error) {
    // TODO: Handle error, redirect to error page, or provide fallback data
    console.error(error);
  }
};
